import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { adminApi } from "apis";
import type { Patient } from "types";
import { mapPatientDtoToPatient } from "utils";

interface UseAdminPatientDetailResult {
  patient: Patient | null;
  hasRequestedSuccessfullyBefore: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useAdminPatientDetail = (
  patientId: string,
): UseAdminPatientDetailResult => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin-patient-detail", patientId],
    queryFn: async () => {
      if (!patientId) {
        return null;
      }

      return adminApi.getPatientDetailForAdmin({ patientId });
    },
    enabled: Boolean(patientId),
    staleTime: 5 * 60 * 1000,
  });

  const patient = useMemo(() => {
    if (!data?.patient) {
      return null;
    }

    return mapPatientDtoToPatient(data.patient);
  }, [data?.patient]);

  const detailError = useMemo(() => {
    if (!error) {
      return null;
    }
    return error instanceof Error ? error.message : String(error);
  }, [error]);

  return {
    patient,
    hasRequestedSuccessfullyBefore:
      data?.hasRequestedSuccessfullyBefore ?? false,
    loading: isLoading,
    error: detailError,
    refetch,
  };
};

export default useAdminPatientDetail;
